import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap, cx } from '@atlaskit/css';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import type { DragLocationHistory, Input } from '@atlaskit/pragmatic-drag-and-drop/types';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { AUDIO_CONTAINER_MODES } from '../../../types/audioContainerMode';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';

const initialTopPosition = 104;
const initialRightOffset = AUDIO_CONTAINER_MODES.EXPANDED.width + 32; // width + space.400

const styles = cssMap({
	floatingStyles: {
		position: 'fixed',
		// @ts-ignore: There are no space tokens larger than 80px (space.1000)
		top: `${initialTopPosition}px`, // under the top nav
		right: token('space.400'), // Tied to initialRightOffset
		zIndex: 510, // modal
		boxShadow: token('elevation.shadow.overlay'),
		borderRadius: token('radius.xlarge'),
		textAlign: 'center',
		transformOrigin: 'top left',
	},
	idleStyles: {
		cursor: 'grab',
	},
	draggingStyles: {
		cursor: 'grabbing',
	},
});

type Position = {
	top: number;
	left: number;
};

// Prevents the miniplayer from being dragged past the boundaries of a given dimension of the window
const getBoundedDimension = (
	proposedDimension: number,
	minDimension: number,
	maxDimension: number,
): number => {
	if (proposedDimension < minDimension || proposedDimension > maxDimension) {
		return proposedDimension < minDimension ? minDimension : maxDimension;
	}

	return proposedDimension;
};

// Prevent clicking from blurring/closing other UI elements like modals/dialogs
const stopClickPropagation: React.MouseEventHandler<HTMLElement> = (
	event: React.MouseEvent,
): void => event.stopPropagation();

// Get actual position without transformation applied so that cursor position does not shift based on element transform (i.e. rotate)
const getTransformedCursorPosition = (element: HTMLElement, inputLocation: Input): DOMPoint => {
	const elementTransform = window.getComputedStyle(element).transform;
	// Edge 18 is very dated now and we should not be concerned with its lack of support
	// eslint-disable-next-line compat/compat
	const transformMatrix = new DOMMatrixReadOnly(elementTransform);

	// Calculate cursor position relative to the element's top-left in viewport coords
	const elementBoundingRect = element.getBoundingClientRect();
	const mouseX = inputLocation.clientX - elementBoundingRect.left;
	const mouseY = inputLocation.clientY - elementBoundingRect.top;

	return transformMatrix.inverse().transformPoint({ x: mouseX, y: mouseY });
};

// The skew of the transform allows for calculating the difference between the real DOM width/height and that of the rotated
const getRotatedDimensionSkew = (element: HTMLElement) => {
	const elementTransform = window.getComputedStyle(element).transform;
	if (!['', 'none'].includes(elementTransform)) {
		const transformMatrixValues = elementTransform.split('(')[1].split(')')[0].split(',');
		return parseFloat(transformMatrixValues[1]);
	}

	return 0;
};

export type DraggableAudioRenderProps = {
	isDragging: boolean;
};

export type DraggableAudioProps = {
	children: (props: DraggableAudioRenderProps) => ReactNode;
};

export const DraggableAudio = ({ children }: DraggableAudioProps): React.JSX.Element | null => {
	const document = getDocument();
	if (!document) {
		return null;
	}

	return <DraggableAudioInternal document={document}>{children}</DraggableAudioInternal>;
};

export type DraggableAudioInternalProps = {
	children: (props: DraggableAudioRenderProps) => ReactNode;
	document: Document;
};

const DraggableAudioInternal = ({
	children,
	document,
}: DraggableAudioInternalProps): React.JSX.Element => {
	const draggableRef = useRef<HTMLElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [currentPosition, setCurrentPosition] = useState<Position>(() => ({
		top: initialTopPosition,
		left: document.body.clientWidth - initialRightOffset,
	}));

	const { activeAudioContainerMode } = useAudioPlayerStore();

	const setPosition = useCallback(
		(element: HTMLElement, location: DragLocationHistory, isDrop = false) => {
			const initialCursorPoint = getTransformedCursorPosition(element, location.initial.input);
			const currentCursorPoint = getTransformedCursorPosition(element, location.current.input);

			// Difference between the current cursor position with that of the beginning of the drag
			const clientYDelta = currentCursorPoint.y - initialCursorPoint.y;
			const clientXDelta = currentCursorPoint.x - initialCursorPoint.x;

			const elementBoundingRect = element.getBoundingClientRect();

			// Proposed new top and left of the draggable position
			const newTop = currentPosition.top + clientYDelta;
			const newLeft = currentPosition.left + clientXDelta;

			// Prevent the boundaries of the draggable from being dragged beyond the window boundaries
			const rotatedTopDiff = getRotatedDimensionSkew(element) * element.offsetWidth; // TODO: prevent recalculating during drag
			const boundedNewTop = getBoundedDimension(
				newTop,
				0,
				window.innerHeight - elementBoundingRect.height - rotatedTopDiff,
			);

			const rotatedLeftDiff = getRotatedDimensionSkew(element) * element.offsetHeight; // TODO: prevent recalculating during drag
			const boundedNewLeft = getBoundedDimension(
				newLeft,
				rotatedLeftDiff,
				document.body.clientWidth - elementBoundingRect.width,
			);

			if (isDrop) {
				// Reset transform and physically move element in the DOM
				element.style.setProperty('transform', 'initial');
				element.style.setProperty('top', `${boundedNewTop}px`);
				element.style.setProperty('left', `${boundedNewLeft}px`);

				setCurrentPosition({
					top: boundedNewTop,
					left: boundedNewLeft,
				});
			} else {
				const boundedDeltaY = boundedNewTop - currentPosition.top;
				const boundedDeltaX = boundedNewLeft - currentPosition.left;

				// Apply CSS transform to dragged spot without physically moving the element in the DOM
				element.style.setProperty(
					'transform',
					`rotate(4deg) translate(${boundedDeltaX}px, ${boundedDeltaY}px)`,
				);
			}
		},
		[currentPosition, document],
	);

	// Setup drag-and-drop
	useEffect(() => {
		const element = draggableRef.current;
		if (!element) {
			return;
		}

		return combine(
			draggable({
				element,
				onGenerateDragPreview({ nativeSetDragImage }) {
					// We'll drag the actual element so hide the native drag preview
					disableNativeDragPreview({ nativeSetDragImage });
				},
				onDragStart: () => {
					setIsDragging(true);
				},
				onDrag: ({ location }) => {
					setPosition(element, location);
				},
				onDrop: ({ location }) => {
					setPosition(element, location, true);
					setIsDragging(false);
				},
			}),
			dropTargetForElements({
				element: document.body,
			}),
		);
	});

	return (
		<Box
			ref={draggableRef}
			xcss={cx(styles.floatingStyles, isDragging ? styles.draggingStyles : styles.idleStyles)}
			style={{ width: `${activeAudioContainerMode.width}px` }}
			onClick={stopClickPropagation}
			testId="audio-draggable"
			role="none"
		>
			{children({ isDragging })}
		</Box>
	);
};
